import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { MainButton, BUTTON_COLORS } from 'common/buttons';
import { CameraController } from 'networking/controllers/camera-controller';

import { Loader } from 'common/loader';

import { PolygonCanvas, CANVAS_LINES, CANVAS_STATES } from 'common/polygonCanvas';
import { lanthornOrange } from 'assets/stylesheets/colors.scss';

import styles from './calibrationModal.module.scss';

const STEPS = {
  STEP0: 0,
  STEP1: 1,
  STEP2: 2,
};

const canvasStateForStep = {
  [STEPS.STEP0]: CANVAS_STATES.OFF,
  [STEPS.STEP1]: CANVAS_STATES.ADDING,
  [STEPS.STEP2]: CANVAS_STATES.MOVING,
};

const CalibrationModal = ({ camera, setShowCalibrationModal }) => {
  const [imageURI, setImage] = useState();
  const [imgSize, setImgSize] = useState({ x: 300, y: 300 });
  const [finished, setFinished] = useState(false);
  const [step, setStep] = useState(STEPS.STEP0);
  const [loading, setLoading] = useState(true);
  const [squareCreated, setSquareCreated] = useState(false);

  useEffect(() => {
    const fetchImage = async () => {
      const { image, errorCode: error } = await CameraController.getCalibrationImage(
        camera.id,
      );
      if (error) return null;
      setImage(`data:image/jpeg;base64,${image}`);
      return setLoading(false);
    };
    fetchImage();
  }, [camera.id]);

  const imgElement = useRef(null);

  const onLoadImg = () => {
    setImgSize({ x: imgElement.current.naturalWidth, y: imgElement.current.naturalHeight });
  };

  const sendMatrix = async (points) => {
    const { errorCode: error } = await CameraController.setCalibrationMatrix(camera.id, points);
    if (!error) return setShowCalibrationModal(false);
    return setFinished(false);
  };

  const canvasConfig = {
    size: imgSize,
    minPoints: 4,
    maxPoints: 4,
    lineStyle: step === STEPS.STEP2 ? CANVAS_LINES.INFINITE : CANVAS_LINES.OFF,
    color: lanthornOrange,
    maskOutside: false,
  };

  return (
    <div className={styles.card}>
      {
        !loading
          ? (
            <PolygonCanvas
              state={canvasStateForStep[step]}
              returnCanMove={() => setSquareCreated(true)}
              returnPoints={finished && sendMatrix}
              config={canvasConfig}
            >
              <img alt="Camera background" src={imageURI} ref={imgElement} onLoad={onLoadImg} />
            </PolygonCanvas>
          ) : (
            <Loader className={styles.loader} />
          )
      }
      {step === STEPS.STEP0 && <Step0 setStep={setStep} />}
      {step === STEPS.STEP1 && <Step1 setStep={setStep} squareCreated={squareCreated} />}
      {step === STEPS.STEP2 && <Step2 setStep={setStep} setFinished={setFinished} />}
    </div>
  );
};

const Step0 = ({ setStep }) => {
  const setStepOnClick = () => setStep(STEPS.STEP1);
  return (
    <div className={styles.step0}>
      <p className={styles.text1}>Calibrate your camera</p>
      <p className={styles.text2}>
        In order to provide accurate social distancing measurements,
        <br />
        we need you to take the following 2 steps.
      </p>
      <MainButton text="Start" onClick={setStepOnClick} color={BUTTON_COLORS.SKY} />
    </div>
  );
};

const Step1 = ({ setStep, squareCreated }) => {
  const setNextStep = () => setStep(STEPS.STEP2);
  const setPrevStep = () => setStep(STEPS.STEP0);
  return (
    <div className={styles.step0}>
      <p className={styles.text1}>
        Select 4 corners of a 3ft3&quot; x 3ft3&quot; (1m x 1m) square  on the floor.
      </p>
      <p className={styles.text2}>
        You must start drawing by first selecting the upper left point of the square
        <br />
        and adding the other points clockwise.
      </p>
      <div className={styles.stepsButtonContainer}>
        <MainButton text="Prev" className={styles.prevButton} onClick={setPrevStep} color={BUTTON_COLORS.GREY} />
        <MainButton text="Next" onClick={setNextStep} disabled={!squareCreated} color={BUTTON_COLORS.SKY} />
      </div>
    </div>
  );
};

const Step2 = ({ setStep, setFinished }) => {
  const [sending, setSending] = useState(false);
  const setStepOnClick = () => setStep(STEPS.STEP1);
  const submitCanvasData = () => { setSending(true); setFinished(true); };
  return (
    <div>
      <p className={styles.text1}>
        Adjust the points so that the grid you see matches
        <br />
        the ground plane and scale.
      </p>
      <div className={styles.stepsButtonContainer}>
        <MainButton text="Prev" className={styles.prevButton} onClick={setStepOnClick} color={BUTTON_COLORS.GREY} />
        <MainButton text="Finish" onClick={submitCanvasData} loading={sending} color={BUTTON_COLORS.SKY} />
      </div>
    </div>
  );
};

CalibrationModal.propTypes = {
  camera: PropTypes.object.isRequired,
  setShowCalibrationModal: PropTypes.func.isRequired,
};
Step0.propTypes = {
  setStep: PropTypes.func.isRequired,
};
Step1.propTypes = {
  setStep: PropTypes.func.isRequired,
  squareCreated: PropTypes.bool.isRequired,
};
Step2.propTypes = {
  setStep: PropTypes.func.isRequired,
  setFinished: PropTypes.func.isRequired,
};

export { CalibrationModal, STEPS };
